import { Paths } from 'shared/routes/paths';
import { UtmParameters } from 'shared/types/utm-parameters';

export enum QueryParamOptions {
  ClaimToken = 'claimToken',
  OobCode = 'oobCode',
  Premium = 'premium',
  OnboardingPremium = 'onboarding-premium',
  OnboardingDesktop = 'onboarding-desktop',
  Purchase = 'purchase',
  PriceType = 'type',
  ConnectionToken = 'connectionToken',
  Search = 'search',
  C = 'c',
  ExternalId = 'externalId',
  DesktopChallenge = 'desktopChallenge',
  // Global so the google and apple sign-in state carries them back to /login.
  Featurebase = 'featurebase',
  ReturnTo = 'return_to',
  // The one-time link of an email change, mailed by the backend.
  EmailChangeToken = 'token',
}

export const globalQueryParamOptions: (QueryParamOptions | UtmParameters)[] = [
  ...Object.values(QueryParamOptions),
  ...Object.values(UtmParameters),
];

const baseAllowedQueryParamOptions = [
  ...Object.values(UtmParameters),
  QueryParamOptions.ClaimToken,
  QueryParamOptions.OobCode,
  QueryParamOptions.C,
  QueryParamOptions.Premium,
];

export const pathAllowedQueryParamOptions: Record<
  Paths,
  (QueryParamOptions | UtmParameters)[]
> = {
  [Paths.Home]: baseAllowedQueryParamOptions,
  [Paths.Inbox]: baseAllowedQueryParamOptions,
  [Paths.Upcoming]: baseAllowedQueryParamOptions,
  [Paths.AllTasks]: baseAllowedQueryParamOptions,
  [Paths.TaskCreate]: baseAllowedQueryParamOptions,
  [Paths.OpenInApp]: baseAllowedQueryParamOptions,
  [Paths.Completed]: baseAllowedQueryParamOptions,
  [Paths.Timeline]: baseAllowedQueryParamOptions,
  [Paths.Goals]: baseAllowedQueryParamOptions,
  [Paths.GoalsRoadmap]: baseAllowedQueryParamOptions,
  [Paths.GoalsCompleted]: baseAllowedQueryParamOptions,
  [Paths.GoalsArchived]: baseAllowedQueryParamOptions,
  [Paths.GoalsThisYear]: baseAllowedQueryParamOptions,
  [Paths.GoalsNextYear]: baseAllowedQueryParamOptions,
  [Paths.GoalsUnplanned]: baseAllowedQueryParamOptions,
  [Paths.GoalDetail]: baseAllowedQueryParamOptions,
  [Paths.GoalDetailRoadmap]: baseAllowedQueryParamOptions,
  [Paths.GoalDetailProgress]: baseAllowedQueryParamOptions,
  [Paths.GoalDetailTasks]: baseAllowedQueryParamOptions,
  [Paths.GoalTemplates]: [
    ...baseAllowedQueryParamOptions,
    QueryParamOptions.Search,
  ],
  [Paths.GoalsCalendar]: baseAllowedQueryParamOptions,
  [Paths.Projects]: baseAllowedQueryParamOptions,
  [Paths.ProjectDetail]: baseAllowedQueryParamOptions,
  [Paths.ProjectDetailRoadmap]: baseAllowedQueryParamOptions,
  [Paths.ProjectDetailProgress]: baseAllowedQueryParamOptions,
  [Paths.ProjectDetailTasks]: baseAllowedQueryParamOptions,
  [Paths.LifeAreas]: baseAllowedQueryParamOptions,
  [Paths.LifeAreaDetail]: baseAllowedQueryParamOptions,
  [Paths.LifeAreaDetailVision]: baseAllowedQueryParamOptions,
  [Paths.LifeAreaDetailRoadmap]: baseAllowedQueryParamOptions,
  [Paths.LifeAreaDetailTasks]: baseAllowedQueryParamOptions,
  [Paths.Habits]: baseAllowedQueryParamOptions,
  [Paths.HabitsCompleted]: baseAllowedQueryParamOptions,
  [Paths.Login]: [
    ...baseAllowedQueryParamOptions,
    QueryParamOptions.DesktopChallenge,
  ],
  [Paths.Signup]: [
    ...baseAllowedQueryParamOptions,
    QueryParamOptions.DesktopChallenge,
  ],
  [Paths.ForgotPassword]: baseAllowedQueryParamOptions,
  [Paths.ResetPassword]: baseAllowedQueryParamOptions,
  [Paths.CompleteSignup]: baseAllowedQueryParamOptions,
  [Paths.VerifyEmail]: baseAllowedQueryParamOptions,
  [Paths.ConfirmEmailChange]: [
    ...baseAllowedQueryParamOptions,
    QueryParamOptions.EmailChangeToken,
  ],
  [Paths.RevertEmailChange]: [
    ...baseAllowedQueryParamOptions,
    QueryParamOptions.EmailChangeToken,
  ],
  [Paths.Insights]: baseAllowedQueryParamOptions,
  [Paths.TagDetail]: baseAllowedQueryParamOptions,
  [Paths.SmartListDetail]: baseAllowedQueryParamOptions,
  [Paths.Search]: [...baseAllowedQueryParamOptions, QueryParamOptions.Search],
  [Paths.Trash]: baseAllowedQueryParamOptions,
  [Paths.DesktopConnected]: [
    ...baseAllowedQueryParamOptions,
    QueryParamOptions.DesktopChallenge,
  ],
};
